import React, { useEffect, useRef, useState } from "react";
import { FaBrain, FaGoogle, FaPersonRunning, FaSpotify, FaTiktok, FaYoutube } from "react-icons/fa6";
import {
  FiArrowRight,
  FiBell,
  FiBookOpen,
  FiCalendar,
  FiCamera,
  FiCheck,
  FiClock,
  FiHelpCircle,
  FiImage,
  FiMessageCircle,
  FiMonitor,
  FiMusic,
  FiPhoneCall,
  FiSearch,
  FiVideo,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { usePublicLang } from "../../../config/i18n/public";
import { useScrollDirection } from "./Website";
import ContactFooterSection from "../components/contactFooter/ContactFooterSection";
import ScrollFocusCollage from "../components/ScrollFocusCollage";
import WaitlistModal from "../components/WaitlistModal";

const FIRST_TRANSITION_FRAMES = Array.from({ length: 50 }, (_, index) => {
  const frame = String(index).padStart(3, "0");
  return `/homepage/optimized_transitions/first_transition/video_01_${frame}.webp`;
});

const SECOND_TRANSITION_FRAMES = Array.from({ length: 39 }, (_, index) => {
  const frame = String(index).padStart(3, "0");
  return `/homepage/optimized_transitions/second_transition/video_02_${frame}.webp`;
});

const THIRD_TRANSITION_FRAMES = [
  ...Array.from({ length: 21 }, (_, index) => index + 9),
  ...Array.from({ length: 9 }, (_, index) => index + 33),
].map((frameNumber) => {
  const frame = String(frameNumber).padStart(3, "0");
  return `/homepage/optimized_transitions/third_transition/video_03_${frame}.webp`;
});

const FOURTH_TRANSITION_FRAMES = Array.from({ length: 40 }, (_, index) => {
  const frame = String(index).padStart(3, "0");
  return `/homepage/optimized_transitions/fourth_transition/video_04_${frame}.webp`;
});

const FIFTH_TRANSITION_FRAMES = Array.from({ length: 40 }, (_, index) => {
  const frame = String(index).padStart(3, "0");
  return `/homepage/optimized_transitions/fifth_transition/video_05_${frame}.webp`;
});

const SIXTH_TRANSITION_FRAMES = Array.from({ length: 50 }, (_, index) => {
  const frame = String(index).padStart(3, "0");
  return `/homepage/optimized_transitions/sixth_transition/video_06_${frame}.webp`;
});

const TRANSITION_FRAME_SEQUENCES = [
  FIRST_TRANSITION_FRAMES,
  SECOND_TRANSITION_FRAMES,
  THIRD_TRANSITION_FRAMES,
  FOURTH_TRANSITION_FRAMES,
  FIFTH_TRANSITION_FRAMES,
  SIXTH_TRANSITION_FRAMES,
];
const FIRST_TRANSITION_END = 0.22;
const FINAL_TEXT_FADE_IN_END = 0.3;
const FINAL_TEXT_FADE_OUT_START = 0.42;
const FINAL_TEXT_FADE_OUT_END = 0.5;
const SECOND_FINAL_TEXT_FADE_IN_START = 0.52;
const SECOND_FINAL_TEXT_FADE_IN_END = 0.58;
const SECOND_FINAL_TEXT_FADE_OUT_START = 0.62;
const SECOND_FINAL_TEXT_FADE_OUT_END = 0.68;
const SECOND_TRANSITION_START = 0.68;
// Must equal THIRD_TRANSITION_START: any gap holds the last frame (video_02_038)
// on screen with zero visual feedback while scrolling, so the page looks frozen.
const SECOND_TRANSITION_END = 0.78;
const THIRD_TRANSITION_START = 0.78;
const FOURTH_TRANSITION_START = 0.83;
const FIFTH_TRANSITION_START = 0.88;
const FIFTH_TRANSITION_END = 0.93;
const POST_FIFTH_CONTENT_FADE_IN_START = FOURTH_TRANSITION_START;
const POST_FIFTH_CONTENT_FADE_IN_END = FOURTH_TRANSITION_START + 0.01;
const POST_FIFTH_CONTENT_FADE_OUT_START = 0.974;
const POST_FIFTH_CONTENT_FADE_OUT_END = 0.98;
const SIXTH_TRANSITION_START = 0.98;
const PRE_SIXTH_IMAGE_SEQUENCE_END = 0.73;
const SIXTH_TRANSITION_SCROLL_START = SIXTH_TRANSITION_START * PRE_SIXTH_IMAGE_SEQUENCE_END;
const IMAGE_SEQUENCE_END = 0.83;
const FAMILY_FEED_TITLE_START = 0.845;
const FAMILY_FEED_TILE_START = 0.86;
const FAMILY_FEED_TILE_STEP = 0.035;
const FAMILY_FEED_TILE_REVEAL_DURATION = 0.025;
const MOBILE_FAMILY_FEED_TILE_STEP = 0.045;
const MOBILE_FAMILY_FEED_TILE_REVEAL_DURATION = 0.012;
const EVERYDAY_SUPPORT_TILE_START = 0.14;
const EVERYDAY_SUPPORT_TILE_STEP = 0.3;
const EVERYDAY_SUPPORT_TILE_REVEAL_DURATION = 0.28;
const MOBILE_EVERYDAY_SUPPORT_TILE_START = 0.08;
const MOBILE_EVERYDAY_SUPPORT_TILE_STEP = 0.28;
const MOBILE_EVERYDAY_SUPPORT_TILE_REVEAL_DURATION = 0.045;
const FRAME_PRELOAD_AHEAD = 1;
const FRAME_PRELOAD_CONCURRENCY = 4;
const FAMILY_FEED_BACKGROUND_SWITCH_OPACITY = 0.98;
const COLLAGE_INTRO_BUBBLE_LAYOUTS = [
  { left: "8%", top: "15%", width: "clamp(8rem, 11vw, 12rem)", rotate: "-8deg" },
  { left: "27%", top: "22%", width: "clamp(7rem, 8.5vw, 9.75rem)", rotate: "5deg" },
  { left: "73%", top: "24%", width: "clamp(7.75rem, 10vw, 11.5rem)", rotate: "-6deg" },
  { left: "92%", top: "17%", width: "clamp(9.5rem, 12.5vw, 14rem)", rotate: "7deg" },
  { left: "93%", top: "43%", width: "clamp(8.25rem, 10.5vw, 12.25rem)", rotate: "-5deg" },
  { left: "91%", top: "68%", width: "clamp(7rem, 8.75vw, 10rem)", rotate: "4deg" },
  { left: "68%", top: "78%", width: "clamp(8rem, 10.25vw, 11.75rem)", rotate: "-7deg" },
  { left: "50%", top: "90%", width: "clamp(8.5rem, 10.5vw, 12rem)", rotate: "4deg" },
  { left: "32%", top: "78%", width: "clamp(7.75rem, 9.75vw, 11rem)", rotate: "-5deg" },
  { left: "8%", top: "63%", width: "clamp(8rem, 10.25vw, 11.75rem)", rotate: "7deg" },
  { left: "19%", top: "43%", width: "clamp(7rem, 8.75vw, 10rem)", rotate: "-6deg" },
  { left: "50%", top: "10%", width: "clamp(7.5rem, 9vw, 10.5rem)", rotate: "3deg" },
];
const MOBILE_COLLAGE_INTRO_BUBBLES = [
  { bubbleIndex: 0, left: "6%", top: "8%", maxWidth: "13rem" },
  { bubbleIndex: 9, right: "5%", top: "18%", maxWidth: "12rem" },
  { bubbleIndex: 5, left: "9%", top: "28%", maxWidth: "14rem" },
  { bubbleIndex: 8, right: "9%", bottom: "22%", maxWidth: "10rem" },
  { bubbleIndex: 11, left: "7%", bottom: "11%", maxWidth: "12rem" },
  { bubbleIndex: 6, right: "5%", bottom: "4%", maxWidth: "15rem" },
];
const MOBILE_VIDEO_CALL_CARD_STYLES = [
  "bg-[#ebe8ff] text-[#6657f3]",
  "bg-[#def8f5] text-[#20bfca]",
];
const MOBILE_VIDEO_CALL_ICON_MAP = {
  camera: FiCamera,
  display: FiMonitor,
};
const SECOND_FINAL_ICON_MAP = {
  conversation: FiMessageCircle,
  memory: FaBrain,
};
const DESKTOP_STEFFI_TITLE_PILL_CLASS =
  "inline-flex rounded-full bg-[#4285F4]/15 px-3 py-1 text-[#1767d8] ring-1 ring-[#4285F4]/25";

function clamp(value, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

function easeOutCubic(value) {
  return 1 - Math.pow(1 - value, 3);
}

function getSequentialTileOpacity(progress, start, step, revealDuration, index, count) {
  const tileStart = start + index * step;
  const nextTileStart = start + (index + 1) * step;
  const fadeIn = clamp((progress - tileStart) / revealDuration);
  const fadeOut =
    index === count - 1
      ? 1
      : clamp((nextTileStart + revealDuration - progress) / revealDuration);

  return Math.min(fadeIn, fadeOut);
}

function HighlightedText({ text, highlight, className = "" }) {
  if (!text || !highlight || !text.includes(highlight)) return text;

  const parts = text.split(highlight);

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {part}
          {index < parts.length - 1 && <span className={className}>{highlight}</span>}
        </React.Fragment>
      ))}
    </>
  );
}

function preloadImage(src) {
  const image = new Image();
  image.decoding = "async";
  image.src = src;

  if (image.decode) {
    return image.decode().catch(() => {});
  }

  return new Promise((resolve) => {
    image.onload = resolve;
    image.onerror = resolve;
  });
}

async function preloadImagesInBatches(srcs) {
  let cursor = 0;
  const workerCount = Math.min(FRAME_PRELOAD_CONCURRENCY, srcs.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (cursor < srcs.length) {
      const src = srcs[cursor];
      cursor += 1;
      await preloadImage(src);
    }
  });

  await Promise.all(workers);
}

function useIsMobileViewport() {
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);

    updateViewport();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateViewport);
      return () => mediaQuery.removeEventListener("change", updateViewport);
    }

    mediaQuery.addListener(updateViewport);
    return () => mediaQuery.removeListener(updateViewport);
  }, []);

  return isMobileViewport;
}

function BulletList({ items, className = "" }) {
  if (!items?.length) return null;

  return (
    <ul className={["mt-6 flex flex-col gap-4 text-left", className].join(" ")}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-4 text-lg font-extrabold leading-snug text-[#405785]">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#dfe9ff] text-[#4e79e7]">
            <FiCheck aria-hidden className="h-4 w-4" />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function MobileTileCarousel({ children, className = "" }) {
  return (
    <div
      className={["-mx-4 mt-8 snap-x snap-mandatory overflow-x-auto pb-3 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden", className].join(" ")}
      style={{
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
      }}
    >
      <div
        className="flex gap-5"
        style={{
          paddingLeft: "calc((100vw - min(78vw, 22rem)) / 2)",
        }}
      >
        {React.Children.map(children, (child) => (
          <div className="w-[78vw] max-w-[22rem] shrink-0 snap-center" style={{ scrollSnapStop: "always" }}>
            {child}
          </div>
        ))}
        <div
          aria-hidden
          className="shrink-0"
          style={{ width: "calc((100vw - min(78vw, 22rem)) / 2)" }}
        />
      </div>
    </div>
  );
}

function MobileVideoCallSection({ copy }) {
  if (!copy) return null;

  return (
    <section className="relative overflow-hidden bg-[linear-gradient(155deg,_#f8fcff_0%,_#eaf6ff_54%,_#fff2e8_100%)] px-4 pb-16 pt-14">
      <div className="relative z-10 mx-auto max-w-md text-left">
        <span className="inline-flex rounded-2xl bg-[#dfe9ff] px-5 py-2 text-sm font-extrabold uppercase tracking-[0.18em] text-[#2875ef] shadow-[0_12px_40px_-30px_rgba(40,117,239,0.9)]">
          {copy.eyebrow}
        </span>
        <h2 className="mt-8 text-[clamp(2rem,8.5vw,3rem)] font-extrabold leading-[1.04] text-[#080b62]">
          <span>{copy.title}</span>
          <span className="block bg-gradient-to-r from-[#2f5cff] via-[#3e8ff2] to-[#4fc5d7] bg-clip-text text-transparent">
            {copy.highlight}
          </span>
        </h2>
        <p className="mt-8 max-w-[19rem] text-[1.65rem] font-medium leading-snug text-[#080b62]">
          {copy.instructionPrefix}{" "}
          <span className="font-extrabold text-[#2875ef]">{copy.instructionHighlight}</span>{" "}
          {copy.instructionSuffix}
        </p>

        <div className="mt-10 flex flex-col gap-4">
          {copy.cards?.map((card, index) => {
            const Icon = MOBILE_VIDEO_CALL_ICON_MAP[card.icon] || FiVideo;

            return (
              <article
                key={card.title}
                className="flex items-center gap-5 rounded-[1.5rem] bg-white/86 p-5 shadow-[0_22px_70px_-45px_rgba(24,48,111,0.72)] ring-1 ring-white/85 backdrop-blur"
              >
                <span
                  className={[
                    "flex h-[5rem] w-[5rem] shrink-0 items-center justify-center rounded-full",
                    MOBILE_VIDEO_CALL_CARD_STYLES[index % MOBILE_VIDEO_CALL_CARD_STYLES.length],
                  ].join(" ")}
                >
                  <Icon aria-hidden className="h-9 w-9 stroke-[2.3]" />
                </span>
                <span className="min-w-0">
                  <strong className="block whitespace-pre-line text-[1.35rem] font-extrabold leading-tight text-[#080b62]">
                    {card.title}
                  </strong>
                  <span className="mt-3 block text-lg font-medium leading-snug text-[#465987]">
                    {card.text}
                  </span>
                </span>
              </article>
            );
          })}
        </div>

      </div>
    </section>
  );
}

function MobileConversationPartnerSection({ copy, videoSrc }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const tryPlay = () => {
      const promise = video.play();
      if (promise && typeof promise.catch === "function") promise.catch(() => {});
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && video.paused) tryPlay();
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(video);
    const onVisibility = () => {
      if (document.visibilityState === "visible" && video.paused) tryPlay();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  if (!copy) return null;

  return (
    <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.98),_rgba(234,246,255,1)_42%,_rgba(224,241,255,1))] px-4 pb-4 pt-10">
      <div className="relative z-10 mx-auto max-w-md text-left">
        <h2 className="text-[clamp(1.8rem,7.8vw,2.7rem)] font-extrabold leading-[1.02] text-[#08286f]">
          <span className="whitespace-pre-line">{copy.title}</span>
          <span className="mt-4 block whitespace-pre-line bg-gradient-to-r from-[#5967ff] via-[#4d9aff] to-[#54d8bd] bg-clip-text text-transparent">
            {copy.highlight}
          </span>
        </h2>
        <div className="mt-10 overflow-hidden rounded-[1.75rem] shadow-[0_24px_80px_-48px_rgba(24,48,111,0.6)]">
          <video
            ref={videoRef}
            src={videoSrc}
            className="h-[82dvh] w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          />
        </div>
      </div>
    </section>
  );
}

function MobileFamilyFeedTile({ image, index, tile }) {
  return (
    <article className="text-left">
      <div className="overflow-hidden rounded-[1.5rem] border border-white/35 bg-white/18 shadow-[0_24px_80px_-38px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(255,255,255,0.42)] backdrop-blur-xl">
        <div
          className={[
            "flex h-[54dvh] max-h-[28rem] min-h-[18rem] justify-center overflow-hidden bg-[linear-gradient(145deg,_rgba(255,255,255,0.24),_rgba(255,255,255,0.08)_38%,_rgba(255,255,255,0.16))]",
            index === 1 ? "items-start px-4 pt-5" : "items-end px-4 pt-5",
          ].join(" ")}
        >
          <img
            src={image}
            alt=""
            aria-hidden
            className="h-full max-h-full w-full max-w-full object-contain"
          />
        </div>
      </div>
      <div className="px-1 pt-4">
        <h3 className="text-[1.45rem] font-extrabold leading-tight text-[#17306d] drop-shadow-[0_1px_12px_rgba(255,255,255,0.84)]">
          {tile.title}
        </h3>
        <p className="mt-2 text-base font-semibold leading-snug text-[#425b90] drop-shadow-[0_1px_10px_rgba(255,255,255,0.8)]">
          {tile.subtitle}
        </p>
        {tile.supportedLabel && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-bold text-[#17306d] drop-shadow-[0_1px_10px_rgba(255,255,255,0.78)]">
            <span>{tile.supportedLabel}</span>
            <span className="inline-flex items-center gap-2">
              <FaYoutube aria-label="YouTube" className="h-6 w-6 text-[#ff0033]" />
              <FaTiktok aria-label="TikTok" className="h-5 w-5 text-[#111827]" />
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

function MobileEverydaySupportTile({ image, imageClassName = "", tile }) {
  return (
    <article className="text-left">
      <div className="overflow-hidden rounded-[1.5rem] border border-white/35 bg-white/18 shadow-[0_24px_80px_-38px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(255,255,255,0.42)] backdrop-blur-xl">
        <div
          className={[
            "flex h-[48dvh] max-h-[26rem] min-h-[16rem] items-center justify-center overflow-hidden bg-[linear-gradient(145deg,_rgba(255,255,255,0.24),_rgba(255,255,255,0.08)_38%,_rgba(255,255,255,0.16))] px-4 py-3",
            imageClassName,
          ].join(" ")}
        >
          {image && (
            <img
              src={image}
              alt=""
              aria-hidden
              className="h-full max-h-full w-full max-w-full rounded-[1.35rem] object-contain"
            />
          )}
        </div>
      </div>
      <div className="px-1 pt-4">
        <h3 className="text-[1.45rem] font-extrabold leading-tight text-[#17306d] drop-shadow-[0_1px_12px_rgba(255,255,255,0.84)]">
          {tile.title}
        </h3>
        {tile.subtitle && (
          <p className="mt-2 text-base font-semibold leading-snug text-[#425b90] drop-shadow-[0_1px_10px_rgba(255,255,255,0.8)]">
            {tile.subtitleHighlight && tile.subtitle.includes(tile.subtitleHighlight)
              ? (() => {
                  const [before, after] = tile.subtitle.split(tile.subtitleHighlight);
                  return (
                    <>
                      {before}
                      <span className="font-extrabold text-[#f97316]">{tile.subtitleHighlight}</span>
                      {after}
                    </>
                  );
                })()
              : tile.subtitle}
          </p>
        )}
      </div>
    </article>
  );
}

function MobileActivationRoutineSection({ onWaitlistClick, routineCopy, waitlistLabel, steffiImageSrc }) {
  const cards = [
    {
      title: routineCopy.gamesTitle,
      description: routineCopy.gamesDescription,
      icon: <FaBrain aria-hidden className="h-12 w-12" />,
    },
    {
      title: routineCopy.physicalTitle,
      description: routineCopy.physicalDescription,
      icon: <FaPersonRunning aria-hidden className="h-12 w-12" />,
    },
    {
      title: routineCopy.companionTitle,
      description: routineCopy.companionDescription,
      icon: <img src="/homepage/friends.png" alt="" className="h-14 w-14 object-contain" />,
    },
  ];

  return (
    <section className="bg-black px-4 py-16 text-white md:hidden">
      <div className="mx-auto max-w-md">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
          {routineCopy.eyebrow}
        </p>
        <h2 className="mt-3 text-4xl font-extrabold leading-tight">{routineCopy.title}</h2>
        <p className="mt-4 text-base font-semibold leading-7 text-white/74">
          {routineCopy.description}
        </p>
        <MobileTileCarousel className="mt-8">
          {cards.map((card) => (
            <article
              key={card.title}
              className="min-h-[18rem] rounded-[1.5rem] border border-white/18 bg-white/[0.08] p-5 shadow-[0_30px_90px_-48px_rgba(255,255,255,0.7)] ring-1 ring-white/10 backdrop-blur-xl"
            >
              <span className="mb-4 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl border border-white/24 text-[#e0f2fe] ring-1 ring-white/10">
                {card.icon}
              </span>
              <h3 className="text-xl font-extrabold leading-tight">{card.title}</h3>
              <p className="mt-2 text-sm font-semibold leading-6 text-white/76">
                {card.description}
              </p>
            </article>
          ))}
        </MobileTileCarousel>
        <img
          src={steffiImageSrc}
          alt=""
          aria-hidden
          className="mt-10 h-auto w-full object-contain"
        />
        <button
          type="button"
          onClick={onWaitlistClick}
          className="mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4285F4] px-5 py-3 text-base font-semibold text-white shadow-md"
        >
          {waitlistLabel}
          <FiArrowRight aria-hidden className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}

export default function FamilyLandingPlaceholder() {
  const { copy } = usePublicLang();
  const c = copy.familyLanding;
  const assets = c.assets;
  const isMobileViewport = useIsMobileViewport();
  const sectionRef = useRef(null);
  const everydaySectionRef = useRef(null);
  const collageIntroRef = useRef(null);
  const preloadedTransitionIndexesRef = useRef(new Set());
  const [scrollProgress, setScrollProgress] = useState(0);
  const [everydayScrollProgress, setEverydayScrollProgress] = useState(0);
  const [collageIntroExplosionProgress, setCollageIntroExplosionProgress] = useState(0);
  const scrollDir = useScrollDirection(8);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const openWaitlist = () => setWaitlistOpen(true);
  const closeWaitlist = () => setWaitlistOpen(false);
  const pastFamilyFeed = scrollProgress >= FAMILY_FEED_TITLE_START;
  const showHeader = !pastFamilyFeed || scrollDir === "up";
  const imageScrollProgress =
    scrollProgress <= SIXTH_TRANSITION_SCROLL_START
      ? clamp(scrollProgress / PRE_SIXTH_IMAGE_SEQUENCE_END)
      : clamp(
          SIXTH_TRANSITION_START +
            ((scrollProgress - SIXTH_TRANSITION_SCROLL_START) /
              (IMAGE_SEQUENCE_END - SIXTH_TRANSITION_SCROLL_START)) *
              (1 - SIXTH_TRANSITION_START)
        );
  const sixthTransitionProgress = clamp(
    (imageScrollProgress - SIXTH_TRANSITION_START) / (1 - SIXTH_TRANSITION_START)
  );
  const fifthTransitionProgress = clamp(
    (imageScrollProgress - FIFTH_TRANSITION_START) / (FIFTH_TRANSITION_END - FIFTH_TRANSITION_START)
  );
  const fourthTransitionProgress = clamp(
    (imageScrollProgress - FOURTH_TRANSITION_START) / (FIFTH_TRANSITION_START - FOURTH_TRANSITION_START)
  );
  const thirdTransitionProgress = clamp(
    (imageScrollProgress - THIRD_TRANSITION_START) / (FOURTH_TRANSITION_START - THIRD_TRANSITION_START)
  );
  const secondTransitionProgress = clamp(
    (imageScrollProgress - SECOND_TRANSITION_START) / (SECOND_TRANSITION_END - SECOND_TRANSITION_START)
  );
  const activeTransitionIndex =
    imageScrollProgress >= SIXTH_TRANSITION_START
      ? 5
      : imageScrollProgress >= FIFTH_TRANSITION_START
        ? 4
        : imageScrollProgress >= FOURTH_TRANSITION_START
          ? 3
          : imageScrollProgress >= THIRD_TRANSITION_START
            ? 2
            : imageScrollProgress >= SECOND_TRANSITION_START
              ? 1
              : 0;
  const currentFrames = TRANSITION_FRAME_SEQUENCES[activeTransitionIndex];
  const currentFrameProgress =
    imageScrollProgress >= SIXTH_TRANSITION_START
      ? sixthTransitionProgress
      : imageScrollProgress >= FIFTH_TRANSITION_START
        ? fifthTransitionProgress
        : imageScrollProgress >= FOURTH_TRANSITION_START
          ? fourthTransitionProgress
          : imageScrollProgress >= THIRD_TRANSITION_START
            ? thirdTransitionProgress
            : imageScrollProgress >= SECOND_TRANSITION_START
              ? secondTransitionProgress
              : clamp(imageScrollProgress / FIRST_TRANSITION_END);
  const frameIndex = Math.min(
    currentFrames.length - 1,
    Math.round(currentFrameProgress * (currentFrames.length - 1))
  );
  const heroContentOpacity = clamp(1 - imageScrollProgress / 0.22);
  const backgroundOverlayOpacity = clamp(1 - imageScrollProgress / FIRST_TRANSITION_END);
  const finalContentFadeIn = clamp(
    (imageScrollProgress - FIRST_TRANSITION_END) / (FINAL_TEXT_FADE_IN_END - FIRST_TRANSITION_END)
  );
  const finalContentFadeOut = clamp(
    (FINAL_TEXT_FADE_OUT_END - imageScrollProgress) / (FINAL_TEXT_FADE_OUT_END - FINAL_TEXT_FADE_OUT_START)
  );
  const finalContentOpacity = Math.min(finalContentFadeIn, finalContentFadeOut);
  const secondFinalContentFadeIn = clamp(
    (imageScrollProgress - SECOND_FINAL_TEXT_FADE_IN_START) /
      (SECOND_FINAL_TEXT_FADE_IN_END - SECOND_FINAL_TEXT_FADE_IN_START)
  );
  const secondFinalContentFadeOut = clamp(
    (SECOND_FINAL_TEXT_FADE_OUT_END - imageScrollProgress) /
      (SECOND_FINAL_TEXT_FADE_OUT_END - SECOND_FINAL_TEXT_FADE_OUT_START)
  );
  const secondFinalContentOpacity = Math.min(secondFinalContentFadeIn, secondFinalContentFadeOut);
  const postFifthContentFadeIn = clamp(
    (imageScrollProgress - POST_FIFTH_CONTENT_FADE_IN_START) /
      (POST_FIFTH_CONTENT_FADE_IN_END - POST_FIFTH_CONTENT_FADE_IN_START)
  );
  const postFifthContentFadeOut = clamp(
    (POST_FIFTH_CONTENT_FADE_OUT_END - imageScrollProgress) /
      (POST_FIFTH_CONTENT_FADE_OUT_END - POST_FIFTH_CONTENT_FADE_OUT_START)
  );
  const postFifthContentOpacity = Math.min(postFifthContentFadeIn, postFifthContentFadeOut);
  const familyFeedTitleOpacity = clamp((scrollProgress - FAMILY_FEED_TITLE_START) / 0.025);
  const familyFeedTiles = c.familyFeedTiles || [];
  const familyFeedTileImages = [
    assets.familyFeedPhotosTile,
    assets.familyFeedChatTile,
    assets.familyFeedMediaTile,
  ];
  const secondFinalDesktop = c.secondFinalDesktop || {
    headlineName: "",
    headlineText: c.secondFinalTitle,
    cards: (c.secondFinalBullets || []).map((text, index) => ({
      icon: index === 0 ? "memory" : "conversation",
      text,
    })),
  };
  const postFifthDesktop = c.postFifthDesktop || {
    headlinePrefix: "",
    headlineHighlight: "",
    headlineSuffix: c.postFifthTitle,
    leadPrefix: "",
    leadHighlight: "",
    leadSuffix: "",
    cards: (c.postFifthBullets || []).map((text, index) => ({
      icon: index === 0 ? "display" : "camera",
      text,
    })),
  };
  const everydaySupportTiles = c.everydaySupportTiles || [];
  const collageIntroSupportedServices = c.collageIntroSupportedServices || [];
  const collageIntroSupportedIcons = [FaSpotify, FaYoutube, FaGoogle];
  const collageIntroSupportedIconClasses = [
    "text-[#1DB954]",
    "text-[#ff0033]",
    "text-[#4285F4]",
  ];
  const collageIntroBubbles = c.collageIntroBubbles || [];
  const collageIntroBubbleIcons = [
    FiBookOpen,
    FiMusic,
    FiMusic,
    FiPhoneCall,
    FiMessageCircle,
    FiBell,
    FiSearch,
    FiVideo,
    FiCalendar,
    FiHelpCircle,
    FiImage,
    FiClock,
  ];
  const familyFeedTileOpacities = familyFeedTiles.map((_, index) =>
    clamp(
      (scrollProgress - (FAMILY_FEED_TILE_START + index * FAMILY_FEED_TILE_STEP)) /
        FAMILY_FEED_TILE_REVEAL_DURATION
    )
  );
  const mobileFamilyFeedTileOpacities = familyFeedTiles.map((_, index) =>
    getSequentialTileOpacity(
      scrollProgress,
      FAMILY_FEED_TILE_START,
      MOBILE_FAMILY_FEED_TILE_STEP,
      MOBILE_FAMILY_FEED_TILE_REVEAL_DURATION,
      index,
      familyFeedTiles.length
    )
  );
  const activeFamilyFeedBackgroundIndex = familyFeedTileOpacities.reduce(
    (activeIndex, opacity, index) =>
      opacity >= FAMILY_FEED_BACKGROUND_SWITCH_OPACITY ? index : activeIndex,
    -1
  );
  const activeTransitionBackground =
    activeFamilyFeedBackgroundIndex >= 0
      ? assets.familyFeedBackgrounds[activeFamilyFeedBackgroundIndex]
      : currentFrames[frameIndex];
  const everydaySupportTileOpacities = everydaySupportTiles.map((_, index) =>
    clamp(
      (everydayScrollProgress - (EVERYDAY_SUPPORT_TILE_START + index * EVERYDAY_SUPPORT_TILE_STEP)) /
        EVERYDAY_SUPPORT_TILE_REVEAL_DURATION
    )
  );
  const mobileEverydaySupportTileOpacities = everydaySupportTiles.map((_, index) =>
    getSequentialTileOpacity(
      everydayScrollProgress,
      MOBILE_EVERYDAY_SUPPORT_TILE_START,
      MOBILE_EVERYDAY_SUPPORT_TILE_STEP,
      MOBILE_EVERYDAY_SUPPORT_TILE_REVEAL_DURATION,
      index,
      everydaySupportTiles.length
    )
  );
  const bubbleExplosionProgress = easeOutCubic(collageIntroExplosionProgress);

  useEffect(() => {
    if (isMobileViewport) {
      setScrollProgress(0);
      setEverydayScrollProgress(0);
      setCollageIntroExplosionProgress(0);
      return undefined;
    }

    let animationFrame = 0;

    function updateProgress() {
      const section = sectionRef.current;
      const everydaySection = everydaySectionRef.current;
      const collageIntroSection = collageIntroRef.current;

      if (section) {
        const rect = section.getBoundingClientRect();
        const scrollableDistance = Math.max(section.offsetHeight - window.innerHeight, 1);
        setScrollProgress(clamp(-rect.top / scrollableDistance));
      }

      if (everydaySection) {
        const rect = everydaySection.getBoundingClientRect();
        const scrollableDistance = Math.max(everydaySection.offsetHeight - window.innerHeight, 1);
        setEverydayScrollProgress(clamp(-rect.top / scrollableDistance));
      }

      if (collageIntroSection) {
        const rect = collageIntroSection.getBoundingClientRect();
        const explosionStart = window.innerHeight * 0.34;
        const explosionEnd = window.innerHeight * 0.02;
        setCollageIntroExplosionProgress(
          clamp((explosionStart - rect.top) / (explosionStart - explosionEnd))
        );
      }
    }

    function onScroll() {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(updateProgress);
    }

    updateProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isMobileViewport]);

  useEffect(() => {
    if (isMobileViewport) return;

    const preloadIndexes = Array.from(
      { length: FRAME_PRELOAD_AHEAD + 1 },
      (_, offset) => activeTransitionIndex + offset
    ).filter((index) => index < TRANSITION_FRAME_SEQUENCES.length);

    preloadIndexes.forEach((index) => {
      if (preloadedTransitionIndexesRef.current.has(index)) return;

      preloadedTransitionIndexesRef.current.add(index);
      preloadImagesInBatches(TRANSITION_FRAME_SEQUENCES[index]);
    });
  }, [activeTransitionIndex, isMobileViewport]);

  useEffect(() => {
    [...assets.familyFeedBackgrounds, ...assets.everydaySupportTiles].forEach((src) => {
      const image = new Image();
      image.decoding = "async";
      image.src = src;
    });
  }, [assets.everydaySupportTiles, assets.familyFeedBackgrounds]);

  return (
    <main className="bg-[#eef3fb] text-[#0b1b2d]">
      <div
        className={[
          "sticky top-0 z-50 bg-[#eef3fb]/82 backdrop-blur-md sm:fixed sm:left-0 sm:right-0 sm:bg-transparent sm:backdrop-blur-0",
          "sm:transition-all sm:duration-500 sm:ease-out sm:will-change-transform",
          showHeader ? "sm:translate-y-0 sm:opacity-100" : "sm:pointer-events-none sm:-translate-y-full sm:opacity-0",
        ].join(" ")}
      >
        <header>
          <div className="mx-auto flex w-full max-w-[1480px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
            <Link to="/" className="flex items-center gap-2 hover:opacity-85">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm">
                <img src="/steffi/logo.png" alt="SteffiCare Logo" className="h-10 w-10 object-contain" />
              </div>
              <span className="text-xl font-bold text-[#18306f] sm:text-white">SteffiCare</span>
            </Link>
            <Link
              to="/family/login"
              className="inline-flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 text-sm font-semibold text-[#18306f] shadow-sm ring-1 ring-white/40 sm:transition sm:hover:bg-white"
            >
              {copy.landing.nav_login}
            </Link>
          </div>
        </header>
      </div>
      <div className="sm:hidden">
        <section className="relative min-h-[calc(100dvh-4.25rem)] overflow-hidden bg-[#eaf6ff] px-4 py-16 text-center">
          <img
            src={assets.mobileHeroBackground}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(234,246,255,0.28)_0%,_rgba(234,246,255,0.52)_48%,_rgba(234,246,255,0.86)_100%)]"
          />
          <div className="relative z-10 mx-auto flex min-h-[calc(100dvh-12rem)] max-w-md flex-col items-center justify-center">
            <h1 className="text-4xl font-extrabold leading-tight text-[#18306f]">
              {c.title}
            </h1>
            <p className="mt-5 text-base font-semibold leading-7 text-[#445a88]">
              {c.subtitle}
            </p>
            <button
              type="button"
              onClick={openWaitlist}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4285F4] px-5 py-3 text-base font-semibold text-white shadow-md"
            >
              {c.login}
              <FiArrowRight aria-hidden className="h-5 w-5" />
            </button>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#eaf6ff] px-4 pb-4 pt-16">
          <div className="relative z-10 mx-auto max-w-md text-left">
            <h2 className="text-3xl font-extrabold leading-tight text-[#18306f]">
              <span>{c.finalTitlePrefix} </span>
              <span className="inline-flex rounded-full bg-[#4285F4]/15 px-3 py-1 text-[#1767d8] ring-1 ring-[#4285F4]/25">
                {c.finalTitleHighlight}
              </span>
              <span> {c.finalTitleSuffix}</span>
            </h2>
            <BulletList items={c.finalNote ? [c.finalNote] : []} />
            <div className="-mx-4 mt-8">
              <img
                src={assets.mobileVoiceBackground}
                alt=""
                aria-hidden
                className="h-auto w-full scale-110 object-contain"
              />
            </div>
          </div>
        </section>

        <MobileConversationPartnerSection copy={c.secondFinalMobile} videoSrc={assets.mobileConversationVideo} />

        <div className="relative z-0">
          <div className="sticky top-[-15lvh] z-0 [transform:translate3d(0,0,0)] [will-change:transform]">
            <MobileVideoCallSection copy={c.postFifthMobile} />
          </div>
          <div className="relative z-10 overflow-hidden [transform:translate3d(0,0,1px)] [will-change:transform]">
            <img
              src={assets.familyFeedWallpaper}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <section className="relative z-10 px-4 py-16">
            <div className="mx-auto max-w-md">
              <div className="mb-10">
                <img
                  src={assets.mobileFamilyFeedDetails}
                  alt=""
                  aria-hidden
                  className="h-auto w-full object-contain"
                />
              </div>
              <h2 className="text-[2.35rem] font-extrabold leading-tight text-white drop-shadow-[0_4px_22px_rgba(0,0,0,0.38)]">
                {c.familyFeedTitle}
              </h2>
              <MobileTileCarousel>
                {familyFeedTiles.map((tile, index) => (
                  <MobileFamilyFeedTile
                    key={tile.title}
                    image={assets.mobileFamilyFeedTiles[index]}
                    index={index}
                    tile={tile}
                  />
                ))}
              </MobileTileCarousel>
            </div>
          </section>

          <div aria-hidden className="relative z-10 h-10 w-full bg-[#e4b686]" />

          <section className="relative z-10 px-4 pb-16 pt-8">
            <div className="mx-auto max-w-md">
              <h2 className="text-[2.35rem] font-extrabold leading-tight text-white drop-shadow-[0_4px_22px_rgba(0,0,0,0.38)]">
                {c.everydaySupportTitle}
              </h2>
              {everydaySupportTiles[0] && (
                <div className="mt-8">
                  <MobileEverydaySupportTile
                    image={assets.everydaySupportTiles[0]}
                    imageClassName="h-[34dvh] max-h-[18rem] min-h-[12rem]"
                    tile={everydaySupportTiles[0]}
                  />
                </div>
              )}
              <MobileTileCarousel className="mt-12">
                {everydaySupportTiles.slice(1).map((tile, index) => (
                  <MobileEverydaySupportTile
                    key={tile.title}
                    image={assets.everydaySupportTiles[index + 1]}
                    tile={tile}
                  />
                ))}
              </MobileTileCarousel>
            </div>
          </section>
          </div>
        </div>

        <section className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-black px-4 py-16 text-white">
          <div
            aria-hidden
            className="absolute inset-0 bg-[radial-gradient(circle_at_50%_46%,_rgba(255,255,255,0.08),_transparent_34%)]"
          />
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
            {MOBILE_COLLAGE_INTRO_BUBBLES.map(({ bubbleIndex, ...layout }) => {
              const bubble = collageIntroBubbles[bubbleIndex];
              if (!bubble) return null;

              return (
                <span
                  key={bubble.title}
                  className="absolute rounded-full border border-white/14 bg-white/[0.06] px-4 py-2 text-sm font-semibold leading-tight text-white/55 opacity-80 blur-[5px] shadow-[0_0_26px_rgba(255,255,255,0.16),inset_0_1px_0_rgba(255,255,255,0.18)] backdrop-blur-xl"
                  style={layout}
                >
                  {bubble.prompt}
                </span>
              );
            })}
          </div>
          <div className="relative z-10 mx-auto max-w-md bg-black/45 px-2 py-8 text-center backdrop-blur-[2px]">
            <h2 className="whitespace-pre-line text-4xl font-extrabold leading-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]">
              {c.collageIntroTitle}
            </h2>
            <p className="mt-5 text-base font-semibold leading-7 text-white/78 drop-shadow-[0_3px_16px_rgba(0,0,0,0.85)]">
              {c.collageIntroSubtitle}
            </p>
            {collageIntroSupportedServices.length > 0 && (
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <span className="w-full text-sm font-semibold text-white/52">
                  {c.collageIntroSupportedLabel}
                </span>
                {collageIntroSupportedServices.map((service, index) => {
                  const Icon = collageIntroSupportedIcons[index] || FaGoogle;

                  return (
                    <span
                      key={service}
                      className="inline-flex items-center gap-2 rounded-full border border-white/14 bg-white/8 px-4 py-2 text-sm font-semibold text-white/88"
                    >
                      <Icon
                        aria-hidden
                        className={[
                          "h-5 w-5",
                          collageIntroSupportedIconClasses[index] || "text-white",
                        ].join(" ")}
                      />
                      {service}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
      <section ref={sectionRef} className="relative z-20 hidden h-[1800lvh] sm:block">
        <div className="sticky top-0 h-[100dvh] overflow-visible">
          <img
            src={activeTransitionBackground}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(255,255,255,0.42)_0%,_rgba(238,243,251,0.34)_48%,_rgba(238,243,251,0.78)_100%)]"
            style={{ opacity: backgroundOverlayOpacity }}
          />

          <div
            className="pointer-events-none absolute inset-0 z-10 px-4 text-center transition-opacity duration-200 sm:px-6"
            style={{
              opacity: finalContentOpacity,
              transform: `translateY(${(1 - finalContentOpacity) * 18}px)`,
            }}
          >
            <div className="mx-auto flex h-full w-full max-w-[1480px] items-center px-2 pb-16 pt-20 text-left sm:px-6 sm:pt-24 lg:px-8">
              <div className="w-full max-w-[34.5rem] min-h-[32rem] -translate-y-12">
                <h2 className="whitespace-pre-line text-[clamp(2.25rem,3.55vw,3.65rem)] font-extrabold leading-[1.04] text-[#07186c] drop-shadow-[0_7px_26px_rgba(255,255,255,0.72)]">
                  <span>{c.finalTitlePrefix} </span>
                  <span className={DESKTOP_STEFFI_TITLE_PILL_CLASS}>
                    {c.finalTitleHighlight}
                  </span>
                  <span> {c.finalTitleSuffix}</span>
                </h2>
              </div>
            </div>
            {c.finalNote && (
              <ul className="absolute bottom-8 left-4 flex max-w-[34rem] flex-col gap-5 px-4 text-left sm:bottom-12 sm:left-6 lg:left-[calc((100vw-1480px)/2+2rem)] lg:max-w-[46rem]">
                <li className="flex items-start gap-4 text-lg font-extrabold leading-snug text-[#405785] sm:text-2xl">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#dfe9ff] text-[#4e79e7]">
                    <FiCheck aria-hidden className="h-4 w-4" />
                  </span>
                  <span>{c.finalNote}</span>
                </li>
                <li aria-hidden className="invisible flex items-start gap-4 text-lg font-extrabold leading-snug sm:text-2xl">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                    <FiCheck aria-hidden className="h-4 w-4" />
                  </span>
                  <span>placeholder</span>
                </li>
              </ul>
            )}
          </div>

          <div
            className="pointer-events-none absolute inset-0 z-10 px-4 text-center transition-opacity duration-200 sm:px-6"
            style={{
              opacity: secondFinalContentOpacity,
              transform: `translateY(${(1 - secondFinalContentOpacity) * 18}px)`,
            }}
          >
            <div className="mx-auto flex h-full w-full max-w-[1480px] items-center px-2 pb-16 pt-20 text-left sm:px-6 sm:pt-24 lg:px-8">
              <div className="w-full max-w-[34.5rem]">
                <h2 className="text-[clamp(2.25rem,3.55vw,3.65rem)] font-extrabold leading-[1.04] text-[#07186c] drop-shadow-[0_7px_26px_rgba(255,255,255,0.72)]">
                  {secondFinalDesktop.headlineName && (
                    <span className={`${DESKTOP_STEFFI_TITLE_PILL_CLASS} mr-3`}>
                      {secondFinalDesktop.headlineName}
                    </span>
                  )}
                  {secondFinalDesktop.headlineText}
                </h2>
                <div className="mt-8 h-1.5 w-24 rounded-full bg-[linear-gradient(90deg,_#1f5cff,_#15d9df)] shadow-[0_8px_28px_rgba(21,142,223,0.32)]" />
                {secondFinalDesktop.cards?.length > 0 && (
                  <div className="mt-10 flex max-w-[34rem] flex-col gap-4">
                    {secondFinalDesktop.cards.map((card, index) => {
                      const Icon = SECOND_FINAL_ICON_MAP[card.icon] || FiCheck;

                      return (
                        <article
                          key={card.text}
                          className="flex min-h-[5.45rem] items-center gap-5 rounded-[1.3rem] border border-white/78 bg-white/82 px-5 py-4 text-[#07186c] shadow-[0_24px_70px_-42px_rgba(50,93,166,0.72)] ring-1 ring-[#dceaff]/80 backdrop-blur-md"
                          style={{
                            transform: `translateY(${(1 - secondFinalContentOpacity) * (10 + index * 4)}px)`,
                          }}
                        >
                          <span className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-[#dfeaff] text-[#1c5cff] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)]">
                            <Icon aria-hidden className="h-8 w-8" />
                          </span>
                          <p className="text-[clamp(1.02rem,1.18vw,1.22rem)] font-extrabold leading-snug">
                            <HighlightedText
                              text={card.text}
                              highlight={card.highlight}
                              className="text-[#1c5cff]"
                            />
                          </p>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-0 z-10 px-4 text-center transition-opacity duration-200 sm:px-6"
            style={{
              opacity: postFifthContentOpacity,
              transform: `translateY(${(1 - postFifthContentOpacity) * 18}px)`,
            }}
          >
            <div className="mx-auto flex h-full w-full max-w-[1480px] items-center px-2 pb-16 pt-20 text-left sm:px-6 sm:pt-24 lg:px-8">
              <div className="w-full max-w-[33.5rem]">
                <h2 className="text-[clamp(2.25rem,3.55vw,3.6rem)] font-extrabold leading-[1.03] text-[#07186c] drop-shadow-[0_7px_26px_rgba(255,255,255,0.72)]">
                  {postFifthDesktop.headlinePrefix && (
                    <span>{postFifthDesktop.headlinePrefix} </span>
                  )}
                  {postFifthDesktop.headlineHighlight && (
                    <span>{postFifthDesktop.headlineHighlight}</span>
                  )}
                  {postFifthDesktop.headlineSuffix && (
                    <span> {postFifthDesktop.headlineSuffix}</span>
                  )}
                </h2>
                <div className="mt-8 h-1.5 w-24 rounded-full bg-[linear-gradient(90deg,_#1f5cff,_#15d9df)] shadow-[0_8px_28px_rgba(21,142,223,0.32)]" />
                <p className="mt-9 max-w-[30rem] text-[clamp(1.3rem,2vw,2rem)] font-extrabold leading-tight text-[#07186c] drop-shadow-[0_5px_22px_rgba(255,255,255,0.72)]">
                  {postFifthDesktop.leadPrefix && <span>{postFifthDesktop.leadPrefix} </span>}
                  {postFifthDesktop.leadHighlight && (
                    <span className="text-[#1c5cff]">{postFifthDesktop.leadHighlight}</span>
                  )}
                  {postFifthDesktop.leadSuffix && <span> {postFifthDesktop.leadSuffix}</span>}
                </p>
                {postFifthDesktop.cards?.length > 0 && (
                  <div className="mt-12 flex max-w-[33.5rem] flex-col gap-4">
                    {postFifthDesktop.cards.map((card, index) => {
                      const Icon = MOBILE_VIDEO_CALL_ICON_MAP[card.icon] || FiCheck;

                      return (
                        <article
                          key={card.text}
                          className="flex min-h-[5.7rem] items-center gap-5 rounded-[1.3rem] border border-white/78 bg-white/82 px-5 py-4 text-[#07186c] shadow-[0_24px_70px_-42px_rgba(50,93,166,0.72)] ring-1 ring-[#dceaff]/80 backdrop-blur-md"
                          style={{
                            transform: `translateY(${(1 - postFifthContentOpacity) * (10 + index * 4)}px)`,
                          }}
                        >
                          <span className="flex h-[4.4rem] w-[4.4rem] shrink-0 items-center justify-center rounded-full bg-[#dfeaff] text-[#1c5cff] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)]">
                            <Icon aria-hidden className="h-8 w-8" />
                          </span>
                          <p className="text-[clamp(1.02rem,1.2vw,1.24rem)] font-extrabold leading-snug">
                            <HighlightedText
                              text={card.text}
                              highlight={card.highlight}
                              className="text-[#1c5cff]"
                            />
                          </p>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="relative z-10 mx-auto flex min-h-[calc(100dvh-80px)] w-full max-w-5xl flex-col items-center justify-center px-4 pb-10 pt-40 text-center transition-opacity duration-150 sm:px-6 sm:pb-12 sm:pt-48"
            style={{
              opacity: heroContentOpacity,
              pointerEvents: heroContentOpacity < 0.12 ? "none" : "auto",
              transform: `translateY(${-imageScrollProgress * 28}px)`,
            }}
          >
            <h1 className="max-w-3xl text-4xl font-extrabold leading-tight text-[#18306f] sm:text-5xl">
              {c.title}
            </h1>
            <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-[#445a88] sm:text-lg">
              {c.subtitle}
            </p>
            <button
              type="button"
              onClick={openWaitlist}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4285F4] px-5 py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#357ae8]"
            >
              {c.login}
              <FiArrowRight aria-hidden className="h-5 w-5" />
            </button>
          </div>

          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center px-4 pt-[4vh] text-center text-white sm:justify-end sm:px-10 sm:pt-[3vh] lg:px-[7vw]"
            style={{
              opacity: familyFeedTitleOpacity,
              transform: `translateY(${(1 - familyFeedTitleOpacity) * 18}px)`,
            }}
          >
            <div className="flex w-full max-w-[68rem] flex-col items-center sm:items-end">
              <h2 className="max-w-[21rem] whitespace-normal text-[clamp(1.8rem,10vw,2.7rem)] font-extrabold leading-tight drop-shadow-[0_4px_22px_rgba(0,0,0,0.38)] sm:max-w-none sm:whitespace-nowrap sm:text-right sm:text-[clamp(1.7rem,5.5vw,5.5rem)]">
                {c.familyFeedTitle}
              </h2>
              <div className="hidden w-full max-w-[68rem] flex-row items-start gap-3 sm:mt-8 sm:flex sm:gap-4">
                {familyFeedTiles.map((tile, index) => {
                  const tileOpacity = familyFeedTileOpacities[index] || 0;

                  return (
                    <div
                      key={tile.title}
                      className="min-w-0 flex-1 text-left"
                      style={{
                        opacity: tileOpacity,
                        transform: `translateY(${(1 - tileOpacity) * 18}px) scale(${0.96 + tileOpacity * 0.04})`,
                      }}
                    >
                      <>
                        <div className="overflow-hidden rounded-[1.75rem] border border-white/35 bg-white/16 shadow-[0_24px_80px_-34px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.38)] backdrop-blur-xl">
                          <div
                            className={[
                              "flex h-64 justify-center overflow-hidden bg-[linear-gradient(145deg,_rgba(255,255,255,0.2),_rgba(255,255,255,0.06)_38%,_rgba(255,255,255,0.13))] sm:h-[24rem] lg:h-[28rem]",
                              index === 1
                                ? "items-start px-3 pt-4 sm:px-5 sm:pt-5"
                                : "items-end px-3 pt-4 sm:px-5 sm:pt-6",
                            ].join(" ")}
                          >
                            <img
                              src={familyFeedTileImages[index]}
                              alt=""
                              aria-hidden
                              className={[
                                "object-contain",
                                index === 1
                                  ? "h-auto w-full max-w-full"
                                  : "h-full max-h-full w-auto max-w-full",
                              ].join(" ")}
                            />
                          </div>
                        </div>
                        <div className="px-2 pt-4 sm:px-4">
                          <p className="text-sm font-extrabold leading-tight text-[#17306d] drop-shadow-[0_1px_12px_rgba(255,255,255,0.78)] sm:text-xl">
                            {tile.title}
                          </p>
                          <p className="mt-2 text-xs font-semibold leading-tight text-[#425b90] drop-shadow-[0_1px_10px_rgba(255,255,255,0.72)] sm:text-base">
                            {tile.subtitle}
                          </p>
                          {tile.supportedLabel && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold text-[#17306d] drop-shadow-[0_1px_10px_rgba(255,255,255,0.72)] sm:text-sm">
                              <span>{tile.supportedLabel}</span>
                              <span className="inline-flex items-center gap-2">
                                <FaYoutube aria-label="YouTube" className="h-5 w-5 text-[#ff0033] sm:h-6 sm:w-6" />
                                <FaTiktok aria-label="TikTok" className="h-4 w-4 text-[#111827] sm:h-5 sm:w-5" />
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    </div>
                  );
                })}
              </div>
              <div className="relative mt-5 h-[calc(100dvh-12rem)] w-full sm:hidden">
                {familyFeedTiles.map((tile, index) => {
                  const tileOpacity = mobileFamilyFeedTileOpacities[index] || 0;

                  return (
                    <div
                      key={tile.title}
                      className="absolute inset-0 flex min-h-0 w-full flex-col justify-center text-left"
                      style={{
                        opacity: tileOpacity,
                        transform: `translateY(${(1 - tileOpacity) * 16}px) scale(${0.965 + tileOpacity * 0.035})`,
                      }}
                    >
                      <div className="overflow-hidden rounded-[1.5rem] border border-white/35 bg-white/16 shadow-[0_24px_80px_-34px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.38)] backdrop-blur-xl">
                        <div
                          className={[
                            "flex h-[47dvh] max-h-[27rem] min-h-[18rem] justify-center overflow-hidden bg-[linear-gradient(145deg,_rgba(255,255,255,0.22),_rgba(255,255,255,0.07)_38%,_rgba(255,255,255,0.14))]",
                            index === 1 ? "items-start px-4 pt-5" : "items-end px-4 pt-5",
                          ].join(" ")}
                        >
                          <img
                            src={familyFeedTileImages[index]}
                            alt=""
                            aria-hidden
                            className={[
                              "object-contain",
                              index === 1
                                ? "h-auto w-full max-w-full"
                                : "h-full max-h-full w-auto max-w-full",
                            ].join(" ")}
                          />
                        </div>
                      </div>
                      <div className="px-2 pt-4">
                        <p className="text-[1.35rem] font-extrabold leading-tight text-[#17306d] drop-shadow-[0_1px_12px_rgba(255,255,255,0.82)]">
                          {tile.title}
                        </p>
                        <p className="mt-2 text-base font-semibold leading-snug text-[#425b90] drop-shadow-[0_1px_10px_rgba(255,255,255,0.78)]">
                          {tile.subtitle}
                        </p>
                        {tile.supportedLabel && (
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-bold text-[#17306d] drop-shadow-[0_1px_10px_rgba(255,255,255,0.76)]">
                            <span>{tile.supportedLabel}</span>
                            <span className="inline-flex items-center gap-2">
                              <FaYoutube aria-label="YouTube" className="h-6 w-6 text-[#ff0033]" />
                              <FaTiktok aria-label="TikTok" className="h-5 w-5 text-[#111827]" />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section ref={everydaySectionRef} className="relative z-0 hidden h-[220dvh] bg-[#eef3fb] sm:block">
        <div className="sticky top-0 min-h-[100dvh] overflow-hidden">
          <img
            src={assets.familyFeedWallpaper}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="relative z-10 flex min-h-[100dvh] items-start justify-center px-4 pt-[7vh] text-left sm:justify-start sm:px-10 sm:pt-[6vh] lg:px-[7vw]">
            <div className="w-full max-w-[88rem]">
              <h2 className="max-w-[21rem] text-[clamp(1.8rem,10vw,2.7rem)] font-extrabold leading-tight text-white drop-shadow-[0_4px_22px_rgba(0,0,0,0.38)] sm:max-w-none sm:text-[clamp(1.7rem,5.5vw,5.5rem)]">
                {c.everydaySupportTitle}
              </h2>
              <div className="hidden w-full grid-cols-1 gap-3 sm:mt-8 sm:grid sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
                {everydaySupportTiles.map((tile, index) => {
                  const tileOpacity = everydaySupportTileOpacities[index] || 0;
                  const tileImage = assets.everydaySupportTiles[index];
                  const isEmergencyTile = index === 0;

                  return (
                    <div
                      key={tile.title}
                      className={`min-w-0 text-left ${isEmergencyTile ? "sm:col-span-2 lg:col-span-2" : ""}`}
                      style={{
                        opacity: tileOpacity,
                        transform: `translateY(${(1 - tileOpacity) * 18}px) scale(${0.96 + tileOpacity * 0.04})`,
                      }}
                    >
                      <div className="overflow-hidden rounded-[1.75rem] border border-white/35 bg-white/16 shadow-[0_24px_80px_-34px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.38)] backdrop-blur-xl">
                        <div
                          className={[
                            "flex h-64 items-center justify-center overflow-hidden bg-[linear-gradient(145deg,_rgba(255,255,255,0.2),_rgba(255,255,255,0.06)_38%,_rgba(255,255,255,0.13))]",
                            isEmergencyTile
                              ? "px-4 py-2 sm:h-[27rem] sm:px-6 sm:py-3 lg:h-[31rem]"
                              : "p-3 sm:h-[27rem] sm:p-5 lg:h-[31rem]",
                          ].join(" ")}
                        >
                          {tileImage && (
                            <img
                              src={tileImage}
                              alt=""
                              aria-hidden
                              className="h-full max-h-full w-full max-w-full rounded-[1.75rem] object-contain"
                            />
                          )}
                        </div>
                      </div>
                      <div className="px-2 pt-4 sm:px-4">
                        <p className="text-sm font-extrabold leading-tight text-[#17306d] drop-shadow-[0_1px_12px_rgba(255,255,255,0.78)] sm:text-xl">
                          {tile.title}
                        </p>
                        {tile.subtitle && (
                          <p className="mt-2 text-xs font-semibold leading-tight text-[#425b90] drop-shadow-[0_1px_10px_rgba(255,255,255,0.72)] sm:text-base">
                            {tile.subtitleHighlight && tile.subtitle.includes(tile.subtitleHighlight)
                              ? (() => {
                                  const [before, after] = tile.subtitle.split(tile.subtitleHighlight);
                                  return (
                                    <>
                                      {before}
                                      <span className="font-extrabold text-[#f97316]">
                                        {tile.subtitleHighlight}
                                      </span>
                                      {after}
                                    </>
                                  );
                                })()
                              : tile.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="relative mt-5 h-[calc(100dvh-12rem)] w-full sm:hidden">
                {everydaySupportTiles.map((tile, index) => {
                  const tileOpacity = mobileEverydaySupportTileOpacities[index] || 0;
                  const tileImage = assets.everydaySupportTiles[index];

                  return (
                    <div
                      key={tile.title}
                      className="absolute inset-0 flex min-h-0 w-full flex-col justify-center text-left"
                      style={{
                        opacity: tileOpacity,
                        transform: `translateY(${(1 - tileOpacity) * 16}px) scale(${0.965 + tileOpacity * 0.035})`,
                      }}
                    >
                      <div className="overflow-hidden rounded-[1.5rem] border border-white/35 bg-white/16 shadow-[0_24px_80px_-34px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.38)] backdrop-blur-xl">
                        <div className="flex h-[43dvh] max-h-[25rem] min-h-[15rem] items-center justify-center overflow-hidden bg-[linear-gradient(145deg,_rgba(255,255,255,0.22),_rgba(255,255,255,0.07)_38%,_rgba(255,255,255,0.14))] px-4 py-3">
                          {tileImage && (
                            <img
                              src={tileImage}
                              alt=""
                              aria-hidden
                              className="h-full max-h-full w-full max-w-full rounded-[1.35rem] object-contain"
                            />
                          )}
                        </div>
                      </div>
                      <div className="px-2 pt-4">
                        <p className="text-[1.35rem] font-extrabold leading-tight text-[#17306d] drop-shadow-[0_1px_12px_rgba(255,255,255,0.82)]">
                          {tile.title}
                        </p>
                        {tile.subtitle && (
                          <p className="mt-2 text-base font-semibold leading-snug text-[#425b90] drop-shadow-[0_1px_10px_rgba(255,255,255,0.78)]">
                            {tile.subtitleHighlight && tile.subtitle.includes(tile.subtitleHighlight)
                              ? (() => {
                                  const [before, after] = tile.subtitle.split(tile.subtitleHighlight);
                                  return (
                                    <>
                                      {before}
                                      <span className="font-extrabold text-[#f97316]">
                                        {tile.subtitleHighlight}
                                      </span>
                                      {after}
                                    </>
                                  );
                                })()
                              : tile.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section
        ref={collageIntroRef}
        className="relative hidden min-h-[100dvh] overflow-hidden bg-black px-4 text-white sm:block sm:px-6 lg:px-8"
      >
        {collageIntroBubbles.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-0">
            {collageIntroBubbles.map((bubble, index) => {
              const Icon = collageIntroBubbleIcons[index] || FiSearch;
              const layout = COLLAGE_INTRO_BUBBLE_LAYOUTS[index % COLLAGE_INTRO_BUBBLE_LAYOUTS.length];
              const targetLeft = Number.parseFloat(layout.left);
              const targetTop = Number.parseFloat(layout.top);
              const targetRotate = Number.parseFloat(layout.rotate);
              const staggeredProgress = clamp((bubbleExplosionProgress - index * 0.018) / 0.88);
              const left = 50 + (targetLeft - 50) * staggeredProgress;
              const top = 50 + (targetTop - 50) * staggeredProgress;
              const rotate = targetRotate * staggeredProgress;
              const scale = 0.28 + staggeredProgress * 0.72;
              const opacity = clamp((staggeredProgress - 0.02) / 0.35);
              const floatDuration = 4.8 + (index % 4) * 0.45;
              const floatDelay = -(index % 5) * 0.5;
              const shouldFloat = staggeredProgress > 0.98;
              const isLongBubble = bubble.prompt.length > 52;

              return (
                <div
                  key={bubble.title}
                  className="absolute"
                  style={{
                    "--bubble-size": layout.width,
                    left: `${left}%`,
                    opacity,
                    top: `${top}%`,
                    transform: `translate(-50%, -50%) rotate(${rotate}deg) scale(${scale})`,
                  }}
                >
                  <div
                    className="flex h-[var(--bubble-size)] w-[var(--bubble-size)] shrink-0 flex-col items-center justify-center overflow-hidden rounded-full border border-white/24 bg-white/[0.11] px-[clamp(0.65rem,1.25vw,1.5rem)] text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-34px_70px_rgba(255,255,255,0.06),0_26px_80px_-42px_rgba(255,255,255,0.9)] ring-1 ring-white/10 backdrop-blur-2xl"
                    style={{
                      animation: shouldFloat
                        ? `collageBubbleFloat ${floatDuration}s ease-in-out ${floatDelay}s infinite alternate`
                        : "none",
                    }}
                  >
                    <Icon
                      aria-hidden
                      className={[
                        "mx-auto shrink-0 text-white/72",
                        isLongBubble
                          ? "mb-[clamp(0.2rem,0.45vw,0.45rem)] h-[clamp(1rem,1.7vw,1.85rem)] w-[clamp(1rem,1.7vw,1.85rem)]"
                          : "mb-[clamp(0.35rem,0.75vw,0.75rem)] h-[clamp(1.25rem,2.15vw,2.25rem)] w-[clamp(1.25rem,2.15vw,2.25rem)]",
                      ].join(" ")}
                    />
                    <p className="max-w-full break-words text-[clamp(0.72rem,1.15vw,1.1rem)] font-extrabold leading-[1.02] text-white">
                      {bubble.title}
                    </p>
                    <p
                      className={[
                        "mt-1 max-w-full break-words font-medium text-white/64",
                        isLongBubble
                          ? "text-[clamp(0.48rem,0.68vw,0.72rem)] leading-[1.02]"
                          : "text-[clamp(0.56rem,0.86vw,0.88rem)] leading-[1.08]",
                      ].join(" ")}
                    >
                      {bubble.prompt}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <style>
          {`
            @keyframes collageBubbleFloat {
              0% {
                transform: translate3d(0, 0, 0);
              }
              100% {
                transform: translate3d(0, -10px, 0);
              }
            }
          `}
        </style>
        <div className="relative z-10 mx-auto flex min-h-[100dvh] max-w-7xl flex-col justify-center py-24 text-center sm:py-32">
          <h2 className="mx-auto max-w-5xl whitespace-pre-line text-4xl font-extrabold leading-tight sm:text-6xl">
            {c.collageIntroTitle}
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-lg font-medium leading-8 text-white/72 sm:text-2xl sm:leading-9">
            {c.collageIntroSubtitle}
          </p>
          {collageIntroSupportedServices.length > 0 && (
            <div className="mx-auto mt-8 flex max-w-4xl flex-wrap items-center justify-center gap-4 text-base font-semibold text-white/82 sm:text-lg">
              <span className="text-white/58">{c.collageIntroSupportedLabel}</span>
              {collageIntroSupportedServices.map((service, index) => {
                const Icon = collageIntroSupportedIcons[index] || FaGoogle;

                return (
                  <span
                    key={service}
                    className="inline-flex items-center gap-2.5 rounded-full border border-white/14 bg-white/8 px-5 py-2.5 text-white/88 shadow-[0_18px_54px_-34px_rgba(255,255,255,0.65)] backdrop-blur-md"
                  >
                    <Icon
                      aria-hidden
                      className={[
                        "h-6 w-6",
                        collageIntroSupportedIconClasses[index] || "text-white",
                      ].join(" ")}
                    />
                    {service}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <ScrollFocusCollage onWaitlistClick={openWaitlist} />
      <MobileActivationRoutineSection
        onWaitlistClick={openWaitlist}
        routineCopy={c.activationRoutine}
        waitlistLabel={c.login}
        steffiImageSrc={assets.mobileHeroBackground}
      />
      <WaitlistModal open={waitlistOpen} onClose={closeWaitlist} />
      <ContactFooterSection />
    </main>
  );
}
